import { COLOR_TOKENS, type ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { Box, type SxProps, type Theme } from '@mui/material';

const VIEWBOX_WIDTH = 520;
const VIEWBOX_HEIGHT = 360;
const PADDING = { top: 12, right: 12, bottom: 76, left: 48 };
const BAR_WIDTH = 28;
const TICK_LABEL_GAP = 8;
const AXIS_LABEL_OFFSET = 16;
const LABEL_ROTATION = -45;
const TICK_FONT_SIZE = 12;

/**
 * Smallest ceiling above `rawMax` that divides into `tickCount` whole steps, so the
 * rendered axis labels stay round. Steps up once more when it lands exactly on
 * `rawMax`, leaving the tallest bar clear of the top gridline.
 */
const niceCeiling = (rawMax: number, tickCount: number): number => {
  if (rawMax <= 0) return tickCount;

  const unit = 10 ** Math.floor(Math.log10(rawMax / tickCount));
  const increment = tickCount * unit;
  const ceiling = Math.ceil(rawMax / increment) * increment;

  return ceiling > rawMax ? ceiling : ceiling + increment;
};

export type ColumnChartBar = Readonly<{
  id: string;
  label: string;
  value: number;
}>;

export type ColumnChartProps = Readonly<{
  bars: readonly ColumnChartBar[];
  /** Y-axis ceiling. Defaults to the tallest bar plus headroom. */
  maxValue?: number;
  color: ColorToken;
  ariaLabel: string;
  tickCount?: number;
}>;

const plotWidth = VIEWBOX_WIDTH - PADDING.left - PADDING.right;
const plotHeight = VIEWBOX_HEIGHT - PADDING.top - PADDING.bottom;
const baseY = PADDING.top + plotHeight;

/** Vertical bars with a labelled y-axis scale and rotated category labels. */
export const ColumnChart = ({
  bars,
  maxValue: maxValueProp,
  color,
  ariaLabel,
  tickCount = 5,
}: ColumnChartProps) => {
  const slot = bars.length > 0 ? plotWidth / bars.length : 0;
  const maxValue =
    maxValueProp ?? niceCeiling(Math.max(...bars.map((bar) => bar.value), 0), tickCount);

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      sx={svgSx}
      role="img"
      aria-label={ariaLabel}
    >
      {Array.from({ length: tickCount + 1 }).map((_, index) => {
        const value = maxValue - (maxValue / tickCount) * index;
        const y = PADDING.top + (plotHeight / tickCount) * index;

        return (
          <g key={`tick-${index}`}>
            <line
              x1={PADDING.left}
              x2={VIEWBOX_WIDTH - PADDING.right}
              y1={y}
              y2={y}
              stroke={COLOR_TOKENS['Colors/Border/border-secondary']}
              strokeWidth={1}
            />
            <text
              x={PADDING.left - TICK_LABEL_GAP}
              y={y + 4}
              textAnchor="end"
              fontSize={TICK_FONT_SIZE}
              fill={COLOR_TOKENS['Colors/Text/text-tertiary']}
            >
              {value.toLocaleString()}
            </text>
          </g>
        );
      })}
      {bars.map((bar, index) => {
        const centerX = PADDING.left + slot * index + slot / 2;
        const ratio = maxValue > 0 ? bar.value / maxValue : 0;
        const barHeight = Math.min(Math.max(ratio, 0), 1) * plotHeight;
        const labelY = baseY + AXIS_LABEL_OFFSET;

        return (
          <g key={bar.id}>
            <rect
              x={centerX - BAR_WIDTH / 2}
              y={baseY - barHeight}
              width={BAR_WIDTH}
              height={barHeight}
              rx={2}
              fill={COLOR_TOKENS[color]}
            />
            <text
              x={centerX}
              y={labelY}
              textAnchor="end"
              fontSize={TICK_FONT_SIZE}
              fill={COLOR_TOKENS['Colors/Text/text-tertiary']}
              transform={`rotate(${LABEL_ROTATION} ${centerX} ${labelY})`}
            >
              {bar.label}
            </text>
          </g>
        );
      })}
    </Box>
  );
};

const svgSx: SxProps<Theme> = {
  width: '100%',
  height: 'auto',
};
